/**
 * indexedDB/index.ts
 *
 * Top-level barrel export for the IndexedDB layer.
 *
 * Import from here instead of reaching into subfolders:
 *
 *   import { appDB, emitIDBChange } from "@/indexedDB"
 *   import { useLiveBlocks, useLiveBlock } from "@/indexedDB"
 *   import { addBlockMutation, updateBlockContentMutation } from "@/indexedDB"
 *   import { SyncWatcher } from "@/indexedDB"
 */

// ─── Database client & event emitter ─────────────────────────────────────────
export { appDB, emitIDBChange, initializeSyncMeta, clearWorkspaceIDBData } from "./db/client";

// ─── TypeScript types / schema ────────────────────────────────────────────────
export type {
  BlockRecord,
  SidebarOrderRecord,
  WorkAreaRecord,
  DataSourceRecord,
  SyncQueueRecord,
  SyncMetaRecord,
  SyncStatus,
  SyncOperationType,
  PageCreatePayload,
  BlockChangesPayload,
  BlockDeletePayload,
  PageMovePayload,
  SidebarReorderPayload,
  WorkAreaOperationPayload,
  DataSourceUpdatePayload,
  UpdatePropertyValuePayload,
  DatabaseSource,
  PageCacheRecord,
} from "./db/schema";

// ─── LRU eviction & IDB cleanup ──────────────────────────────────────────────
export { touchPageCache, runEviction, purgeOrphanRootPages, refreshPageCacheTimestamp, refreshPageCacheAccess, recordPageEditStamp } from "./eviction";

// ─── Raw store helpers (use mutations in components instead) ──────────────────
export {
  getBlock,
  hasBlock,
  getChildren,
  getChildrenBlocks,
  getAllBlocks,
  getPageBlocks,
  getBlocksByParentId,
  addBlock,
  updateBlock,
  removeBlock,
  bulkLoad,
  upsertBlocks,
  markBlockSynced,
  recordBlockSyncFailure,
  toBlockRecord,
  toBlock,
} from "./stores/blocksStore";

export {
  getSidebarOrder,
  saveSidebarOrder,
  updateSidebarOrder,
  markSidebarOrderSynced,
  isSidebarOrderUnsynced,
} from "./stores/sidebarOrderStore";

export {
  getAllWorkAreas,
  getWorkArea,
  bulkLoadWorkAreas,
  addWorkArea,
  updateWorkArea,
  removeWorkArea,
  markWorkAreaSynced,
  recordWorkAreaSyncFailure,
  getUnsyncedWorkAreas,
} from "./stores/workAreasStore";

export {
  getAllDataSources,
  getDataSourceFromDB,
  bulkLoadDataSources,
  addDataSource,
  updateDataSourceInDB,
  patchDataSourcePropertyInDB,
  markDataSourceSynced,
  recordDataSourceSyncFailure,
  getUnsyncedDataSources,
  toDataSourceRecord,
  toDataSource,
} from "./stores/dataSourcesStore";

export {
  enqueueBlockChanges,
  enqueueSidebarReorder,
  enqueueWorkAreaOperation,
  getUnsyncedOps,
  getUnsyncedOpsForNote,
  getPendingBlockIds,
  countPendingOps,
  markOpSynced,
  markOpsSynced,
  recordOpFailure,
  getPermanentlyFailedOps,
  pruneSyncedOps,
  hasPendingDatasourceCreation,
  enqueueReminderUpsert,
  enqueueReminderCancel,
} from "./stores/syncQueueStore";

export {
  getSyncMeta,
  setSyncStatus,
  setPendingCount,
  recordSyncSuccess,
  setActiveWorkspace,
  upsertSyncMeta,
} from "./stores/syncMetaStore";

// ─── Atomic mutations (use these in components) ───────────────────────────────
export {
  createBlockMutation,
  createCollectionMutation,
  addBlockMutation,
  updateBlockContentMutation,
  updateBlockFieldsMutation,
  updatePropertyValueMutation,
  updatePageSettingsMutation,
  deleteBlockMutation,
  deleteTemplateMutation,
  deletePageMutation,
  trashPageMutation,
  restorePageMutation,
  movePageMutation,
  dragAndDropMutation,
  loadBlocksMutation,
  upsertBlocksMutation,
  updateBlockCommentsMutation,
  instantiateTemplateRowMutation,
  isInstantiationPending,
  buildTemplateRowValue,
  mergeInstantiatedRootBlock,
} from "./mutations/blockMutations";

export {
  addPrivatePageMutation,
  removePrivatePageMutation,
  reorderPrivateMutation,
  addPublicPageMutation,
  removePublicPageMutation,
  reorderPublicMutation,
  addWorkAreaPageMutation,
  removeWorkAreaPageMutation,
  reorderWorkAreaMutation,
  addSharedPageMutation,
  removeSharedPageMutation,
  reorderSharedMutation,
  addTemplatePageMutation,
  removeTemplatePageMutation,
  reorderTemplatesMutation,
  removePageFromAllMutation,
  movePageToPrivateMutation,
  movePageToPublicMutation,
  movePageToWorkAreaMutation,
  setSidebarOrderMutation,
  loadSidebarOrderMutation,
  addFavouritePageMutation,
  removeFavouritePageMutation,
  addRootBlocksAfterMutation,
} from "./mutations/sidebarMutations";

export {
  createWorkAreaMutation,
  updateWorkAreaMutation,
  deleteWorkAreaMutation,
  loadWorkAreasMutation,
} from "./mutations/workAreaMutations";

export {
  loadDataSourcesMutation,
  updateDataSourceMutation,
  updateDatasourceSettingsMutation,
  patchDataSourcePropertyMutation,
  createPropertyMutation,
  deletePropertyMutation,
  updatePropertySchemaMutation,
  reorderSchemaMutation,
  updateDatasourcePageSettingsMutation,
} from "./mutations/dataSourceMutations";

export { updateViewMutation, reorderViewsMutation, updateLayoutMutation, updatePropertyVisibilityMutation, updateFilterMutation, updateSortMutation, updateColumnWidthsMutation, updateGroupMutation, addViewMutation, deleteViewMutation, updateChartSettingsMutation, toggleLockMutation, updateAdvancedFilterMutation, updateDashboardSettingsMutation } from "./mutations/viewMutations";

export { cascadeDatasourceDeleteInIDB, purgeDeadDatasourceInIDB } from "./mutations/cascadeDeleteMutations";

// ─── Fine-grained block store (per-entity subscriptions) ─────────────────────
// See BLOCK_STORE_REDESIGN.md. Prefer these over useLiveBlocks/useGlobalBlocks
// for new code: useBlock re-renders only when THAT block changes.
export { blockStore, BlockStore } from "./store/blockStore";
export { useBlock, useChildBlocks, usePages } from "./hooks/useBlock";
export { useResolvedBlocks } from "./hooks/useResolvedBlocks";

// ─── React hooks (live-querying, auto-rerenders) ──────────────────────────────
export { useLiveBlocks } from "./hooks/useLiveBlocks";
export { useLiveBlock, useLiveBlockValue } from "./hooks/useLiveBlock";
export { useLocalSidebarOrder } from "./hooks/useLocalSidebarOrder";
export { useLiveWorkAreas } from "./hooks/useLiveWorkAreas";
export { useLiveDataSources } from "./hooks/useLiveDataSources";
export { useSyncStatus } from "./hooks/useSyncStatus";

// ─── IDB availability status (probe + singleton) ─────────────────────────────
export type { IDBStatus } from "./idbStatus";
export { probeIDB, getIDBStatus, isIDBAvailable, setIDBStatus, isQuotaError } from "./idbStatus";

// ─── Sync engine ──────────────────────────────────────────────────────────────
export { syncWatcher } from "./sync/SyncWatcher";
export { networkMonitor } from "./sync/networkMonitor";
export { retryScheduler, calculateBackoffDelay } from "./sync/retryScheduler";
export { mergeBlockOps, groupOpsByNoteId } from "./sync/operationMerger";
